import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { FeePurposeSchema, type FeePurpose } from '../fee-purpose/fee-purpose';
import { MoneyAmountSchema } from '../money/money';
import { ProductAudienceSchema } from '../product-audience/product-audience';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductKindSchema } from '../product-kind/product-kind';

export const PRODUCT_NAME_MAX_LENGTH = 120;
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 600;

/** How long a store product identifier may be. Apple's and Google's both fit. */
export const STORE_PRODUCT_ID_MAX_LENGTH = 200;

/**
 * A tier's key — `gold`, `silver`. Free-form because tiers are staff-created and
 * `billing.tierCapabilities` is keyed by them; the matrix editor supplies the
 * values so nobody types one twice.
 */
export const TIER_KEY_MAX_LENGTH = 60;

/**
 * The longest subscription a product may sell.
 *
 * Named rather than inlined for `TEST_ATTEMPTS_MIN`'s reason: the admin form
 * clamps its number box from here, so a control can never offer a duration the
 * api refuses. Ten years is past any plausible plan and still short of the
 * arithmetic getting strange.
 */
export const PRODUCT_DURATION_DAYS_MIN = 1;
export const PRODUCT_DURATION_DAYS_MAX = 3650;

export const ProductDurationDaysSchema = z
  .number()
  .int()
  .min(PRODUCT_DURATION_DAYS_MIN)
  .max(PRODUCT_DURATION_DAYS_MAX);

export const TierKeySchema = z.string().trim().min(1).max(TIER_KEY_MAX_LENGTH);

/** Per-locale product copy (N5, W7). Both locales required — U8's parity law. */
export const SubscriptionProductTranslationSchema = z
  .object({
    name: z.string().trim().min(1).max(PRODUCT_NAME_MAX_LENGTH),
    /** The paywall card's supporting line. */
    description: z.string().trim().min(1).max(PRODUCT_DESCRIPTION_MAX_LENGTH),
  })
  .strict();

export type SubscriptionProductTranslation = z.infer<typeof SubscriptionProductTranslationSchema>;

export const SubscriptionProductTranslationsSchema = makeTranslationsSchema(
  SubscriptionProductTranslationSchema,
);

export type SubscriptionProductTranslations = z.infer<typeof SubscriptionProductTranslationsSchema>;

/**
 * The fields whose presence the product's `kind` decides.
 *
 * One flat shape with a refinement rather than a discriminated union, and the
 * choice is deliberate: the row is flat, the admin form is flat, and every
 * holder of the type keeps compiling — a union would make `durationDays`
 * unreadable at forty call sites that only ever see a plan. The refinement is
 * what keeps the flat shape honest, and {@link isSubscriptionProduct} /
 * {@link isFeeProduct} are how a reader narrows to the half it needs.
 */
export interface ProductKindFields {
  kind: 'subscription' | 'fee';
  purpose: FeePurpose | null;
  tier: string | null;
  durationDays: number | null;
}

/** A plan: a tier for a period, granting an entitlement. */
export type SubscriptionPlanFields = ProductKindFields & {
  kind: 'subscription';
  purpose: null;
  tier: string;
  durationDays: number;
};

/** A one-off fee: paid for one purpose, granting nothing. */
export type FeeProductFields = ProductKindFields & {
  kind: 'fee';
  purpose: FeePurpose;
  tier: null;
  durationDays: null;
};

export function isSubscriptionProduct<T extends ProductKindFields>(
  product: T,
): product is T & SubscriptionPlanFields {
  return product.kind === 'subscription';
}

export function isFeeProduct<T extends ProductKindFields>(
  product: T,
): product is T & FeeProductFields {
  return product.kind === 'fee';
}

/**
 * The kind's invariants, applied by every schema that carries the four fields.
 *
 * A plan has a tier and a duration and no purpose; a fee has a purpose and
 * neither of the other two. Stated once here so the read DTO, the write
 * contract and the order snapshot cannot each hold a slightly different idea of
 * what a fee looks like — the table's CHECK constraints say the same thing on
 * the storage side.
 */
export function refineProductKind(value: ProductKindFields, ctx: z.RefinementCtx): void {
  if (value.kind === 'subscription') {
    if (value.durationDays === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationDays'],
        message: 'A subscription needs a duration.',
      });
    }
    if (value.tier === null) {
      ctx.addIssue({ code: 'custom', path: ['tier'], message: 'A subscription needs a tier.' });
    }
    if (value.purpose !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['purpose'],
        message: 'A subscription has no fee purpose.',
      });
    }
    return;
  }

  if (value.purpose === null) {
    ctx.addIssue({ code: 'custom', path: ['purpose'], message: 'A fee needs a purpose.' });
  }
  if (value.durationDays !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['durationDays'],
      message: 'A fee has no duration — it is paid once.',
    });
  }
  if (value.tier !== null) {
    ctx.addIssue({ code: 'custom', path: ['tier'], message: 'A fee grants no tier.' });
  }
}

/**
 * A product staff have authored (`03-payments-iap.md` §Principles) — a
 * subscription plan, or since D9a a one-off **fee**.
 *
 * `name` and `description` are the **caller's locale**, resolved by the reader;
 * the admin edit form reads {@link SubscriptionProductDetailSchema}, which
 * carries every locale (W7).
 *
 * ## The store ids are nullable, and that is not laziness
 *
 * A deployment whose market has no Apple or Google billing sells only through
 * the direct channel, so a product there legitimately has neither id. A
 * deployment that sells both ways fills them in. Requiring them would make the
 * direct-only case unrepresentable — which is precisely the market this
 * platform exists to serve.
 *
 * ## `isActive` is publication, and withdrawing one never revokes anything
 *
 * A member holding an entitlement for a deactivated product keeps it until its
 * period ends: the flag governs what may be *bought*, never what has already
 * been paid for. The same distinction `courses.isActive` draws for progress.
 * A settled fee likewise stays settled.
 */
export const SubscriptionProductSchema = z
  .object({
    id: UuidSchema,
    key: ProductKeySchema,
    kind: ProductKindSchema,
    /** What a `fee` is for; always `null` on a plan. */
    purpose: FeePurposeSchema.nullable(),
    audience: ProductAudienceSchema,
    /**
     * Which tier this product grants. Consulted **only** in `feature` plan mode,
     * where it selects a row of `billing.tierCapabilities`; in `duration` mode
     * any active entitlement unlocks everything and this is inert (N14).
     * `null` on a fee, which grants nothing.
     */
    tier: TierKeySchema.nullable(),
    /** The period a plan runs for; `null` on a fee, which is paid once. */
    durationDays: ProductDurationDaysSchema.nullable(),
    iosProductId: z.string().trim().min(1).max(STORE_PRODUCT_ID_MAX_LENGTH).nullable(),
    androidProductId: z.string().trim().min(1).max(STORE_PRODUCT_ID_MAX_LENGTH).nullable(),
    /** The RevenueCat entitlement this product's purchase grants. */
    rcEntitlementId: z.string().trim().min(1).max(STORE_PRODUCT_ID_MAX_LENGTH).nullable(),
    isActive: z.boolean(),
    /** Paywall order — staff-controlled, not price-sorted. */
    sortOrder: z.number().int().nonnegative(),
    name: z.string(),
    description: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict()
  .superRefine(refineProductKind);

export type SubscriptionProduct = z.infer<typeof SubscriptionProductSchema>;

/**
 * What the admin list and edit form read.
 *
 * `directPrices` carries **every scope's** price, not the caller's: the operator
 * editing the catalogue needs to see that men are charged one figure and women
 * another, and a single resolved number would hide exactly the fact the
 * gender-scoped setting exists to express (U15). `null` for a scope means the
 * product has no direct price there and falls back to global.
 */
export const SubscriptionProductDetailSchema = z
  .object({
    ...SubscriptionProductSchema.shape,
    translations: SubscriptionProductTranslationsSchema,
    directPrices: z
      .object({
        global: MoneyAmountSchema.nullable(),
        male: MoneyAmountSchema.nullable(),
        female: MoneyAmountSchema.nullable(),
      })
      .strict(),
  })
  .strict()
  .superRefine(refineProductKind);

export type SubscriptionProductDetail = z.infer<typeof SubscriptionProductDetailSchema>;
